import styled from "@emotion/styled";
import { FC, ReactNode, useId, useRef } from "react";
import { Modal, type ModalHandle } from "../Modal";
import { useDialogTriggerOpenState } from "../dialog/useDialogTriggerOpenState";
import { PopupDialogCloseButton } from "./PopupDialogCloseButton";
import { PopupDialogHeading } from "./PopupDialogHeading";
import { IconDialogClose } from "../../../icons/IconDialogClose"; // Don't change this. `icons` folder is outside `src` -> results in a type check error without the dots

/**
 * The visible card chrome. `Modal` (alert variant) supplies the overlay,
 * centering, entrance animation, focus trapping and z-index stacking; this
 * just renders the close button / heading / content / actions inside it so the
 * public PopupDialog API and its compact-card design stay unchanged.
 */
const Body = styled.div`
  label: PopupDialog;
  position: relative;
  padding: 1.5rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
`;

// Extends the shared `PopupDialogHeading` with the right-side padding the
// PopupDialog layout needs to keep the heading clear of the close button.
const HeadingWithCloseGutter = styled(PopupDialogHeading)`
  padding-right: 2rem;
`;

const Content = styled.div`
  label: PopupDialogContent;
  padding-top: 1rem;
  color: var(--color-charcoal, #212121);
  font-size: 1rem;
  line-height: 140%;
  letter-spacing: -0.02em;
`;

const Actions = styled.div`
  label: PopupDialogAction;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  align-items: center;
  padding-top: 1.5rem;
`;

interface PopupDialogProps {
  title?: ReactNode;
  actions?: ReactNode | ((opts: { close: () => void }) => ReactNode);
  children?: ReactNode;
  onClose?: () => void;
  actionsGap?: string;
  /** Default true. When false, neither backdrop click nor Escape dismisses. */
  isDismissable?: boolean;
  /**
   * Controlled open state. Optional — when rendered inside a
   * `PopupDialogTrigger` the open state comes from the trigger instead.
   */
  isOpen?: boolean;
  ariaLabel?: string;
  /** Forwarded to the card, so `styled(PopupDialog)` overrides keep working. */
  className?: string;
}

export const PopupDialog: FC<PopupDialogProps> = ({
  title,
  actions,
  children,
  onClose,
  actionsGap,
  isDismissable = true,
  isOpen: controlledIsOpen,
  ariaLabel,
  className
}) => {
  const contentId = useId();
  const modalRef = useRef<ModalHandle>(null);

  const { isOpen, handleDidDismiss } = useDialogTriggerOpenState({
    isOpen: controlledIsOpen,
    onClose
  });

  // Plays the dismiss animation; `onDidDismiss` then settles the open state.
  const close = (): void => modalRef.current?.dismiss();

  return (
    <Modal
      ref={modalRef}
      isOpen={isOpen}
      variant="alert"
      backdropDismiss={isDismissable}
      onDidDismiss={handleDidDismiss}
      className={className}
      ariaLabel={ariaLabel}
    >
      <Body aria-describedby={children ? contentId : undefined}>
        <PopupDialogCloseButton slot="close" aria-label="Close">
          <IconDialogClose aria-hidden={true} />
        </PopupDialogCloseButton>
        {title && (
          <HeadingWithCloseGutter slot="title">{title}</HeadingWithCloseGutter>
        )}
        {children && <Content id={contentId}>{children}</Content>}
        {actions && (
          <Actions style={{ gap: actionsGap ?? "0" }}>
            {typeof actions === "function" ? actions({ close }) : actions}
          </Actions>
        )}
      </Body>
    </Modal>
  );
};
